/**
 * `LAENDER` — ISO-3166-alpha-2 Code+Name-Paare fuer den neuen Land-Dropdown (Adressmodell,
 * WARTESCHLANGE Punkt 7, PR4). Gleiches Muster wie `BUNDESLAENDER` (`@maestro/feiertage`,
 * verwendet in `pages/settings.tsx`): eine flache, sortierte Konstante, Code im Hintergrund,
 * Name sichtbar in der Liste.
 *
 * Reihenfolge laut Konzept § 3.3 (docs/KONZEPT-ADRESSMODELL.md, wiederhergestellt aus Commit
 * `18931c6e` — auf diesem Branch urspruenglich nicht vorhanden, s. Abschlussmeldung): Deutschland/
 * Oesterreich/Schweiz ZUERST (haeufigste Faelle fuer einen deutschen Catering-Betrieb), danach der
 * Rest ALPHABETISCH nach dem DEUTSCHEN Laendernamen (nicht nach ISO-Code). Der DB-seitige
 * CHECK-Constraint (`customers_address_country_code_check` u.a., Migration 422) prueft nur das
 * FORMAT (`^[A-Z]{2}$`), nicht die Zugehoerigkeit zu dieser Liste — ein Land, das hier fehlt, kann
 * also serverseitig trotzdem gespeichert werden (z. B. ueber die Google-Places-Uebernahme); diese
 * Liste ist eine UX-Hilfe fuer den Dropdown, keine harte Validierungsgrenze.
 *
 * Vollstaendigkeit: alle europaeischen Staaten + gaengige internationale Handelspartner/
 * Herkunftslaender. Bewusst KEIN neues npm-Paket (`i18n-iso-countries` u. ae. nicht im Lockfile,
 * s. Konzept-Dokument § 3.3) — eine reine Dropdown-Liste braucht keine vollstaendige, staendig
 * gepflegte ISO-Bibliothek.
 */
export interface Land {
  code: string;
  name: string;
}

export const LAENDER: Land[] = [
  // Haeufigste Faelle zuerst (Konzept § 3.3).
  { code: "DE", name: "Deutschland" },
  { code: "AT", name: "Österreich" },
  { code: "CH", name: "Schweiz" },
  // Rest alphabetisch nach deutschem Laendernamen.
  { code: "AF", name: "Afghanistan" },
  { code: "EG", name: "Ägypten" },
  { code: "AL", name: "Albanien" },
  { code: "DZ", name: "Algerien" },
  { code: "AD", name: "Andorra" },
  { code: "AO", name: "Angola" },
  { code: "AR", name: "Argentinien" },
  { code: "AM", name: "Armenien" },
  { code: "AZ", name: "Aserbaidschan" },
  { code: "ET", name: "Äthiopien" },
  { code: "AU", name: "Australien" },
  { code: "BS", name: "Bahamas" },
  { code: "BH", name: "Bahrain" },
  { code: "BD", name: "Bangladesch" },
  { code: "BB", name: "Barbados" },
  { code: "BE", name: "Belgien" },
  { code: "BZ", name: "Belize" },
  { code: "BJ", name: "Benin" },
  { code: "BT", name: "Bhutan" },
  { code: "BO", name: "Bolivien" },
  { code: "BA", name: "Bosnien und Herzegowina" },
  { code: "BW", name: "Botswana" },
  { code: "BR", name: "Brasilien" },
  { code: "BN", name: "Brunei" },
  { code: "BG", name: "Bulgarien" },
  { code: "BF", name: "Burkina Faso" },
  { code: "BI", name: "Burundi" },
  { code: "CL", name: "Chile" },
  { code: "CN", name: "China" },
  { code: "CR", name: "Costa Rica" },
  { code: "CI", name: "Côte d'Ivoire" },
  { code: "DK", name: "Dänemark" },
  { code: "DO", name: "Dominikanische Republik" },
  { code: "DJ", name: "Dschibuti" },
  { code: "EC", name: "Ecuador" },
  { code: "SV", name: "El Salvador" },
  { code: "EE", name: "Estland" },
  { code: "FJ", name: "Fidschi" },
  { code: "FI", name: "Finnland" },
  { code: "FR", name: "Frankreich" },
  { code: "GA", name: "Gabun" },
  { code: "GM", name: "Gambia" },
  { code: "GE", name: "Georgien" },
  { code: "GH", name: "Ghana" },
  { code: "GR", name: "Griechenland" },
  { code: "GT", name: "Guatemala" },
  { code: "GN", name: "Guinea" },
  { code: "GY", name: "Guyana" },
  { code: "HT", name: "Haiti" },
  { code: "HN", name: "Honduras" },
  { code: "IN", name: "Indien" },
  { code: "ID", name: "Indonesien" },
  { code: "IQ", name: "Irak" },
  { code: "IR", name: "Iran" },
  { code: "IE", name: "Irland" },
  { code: "IS", name: "Island" },
  { code: "IL", name: "Israel" },
  { code: "IT", name: "Italien" },
  { code: "JM", name: "Jamaika" },
  { code: "JP", name: "Japan" },
  { code: "YE", name: "Jemen" },
  { code: "JO", name: "Jordanien" },
  { code: "KH", name: "Kambodscha" },
  { code: "CM", name: "Kamerun" },
  { code: "CA", name: "Kanada" },
  { code: "KZ", name: "Kasachstan" },
  { code: "QA", name: "Katar" },
  { code: "KE", name: "Kenia" },
  { code: "KG", name: "Kirgisistan" },
  { code: "CO", name: "Kolumbien" },
  { code: "CD", name: "Kongo (Dem. Republik)" },
  { code: "CG", name: "Kongo (Republik)" },
  { code: "HR", name: "Kroatien" },
  { code: "CU", name: "Kuba" },
  { code: "KW", name: "Kuwait" },
  { code: "LA", name: "Laos" },
  { code: "LS", name: "Lesotho" },
  { code: "LV", name: "Lettland" },
  { code: "LB", name: "Libanon" },
  { code: "LR", name: "Liberia" },
  { code: "LY", name: "Libyen" },
  { code: "LI", name: "Liechtenstein" },
  { code: "LT", name: "Litauen" },
  { code: "LU", name: "Luxemburg" },
  { code: "MG", name: "Madagaskar" },
  { code: "MW", name: "Malawi" },
  { code: "MY", name: "Malaysia" },
  { code: "MV", name: "Malediven" },
  { code: "ML", name: "Mali" },
  { code: "MT", name: "Malta" },
  { code: "MA", name: "Marokko" },
  { code: "MR", name: "Mauretanien" },
  { code: "MU", name: "Mauritius" },
  { code: "MX", name: "Mexiko" },
  { code: "MD", name: "Moldau" },
  { code: "MC", name: "Monaco" },
  { code: "MN", name: "Mongolei" },
  { code: "ME", name: "Montenegro" },
  { code: "MZ", name: "Mosambik" },
  { code: "MM", name: "Myanmar" },
  { code: "NA", name: "Namibia" },
  { code: "NP", name: "Nepal" },
  { code: "NZ", name: "Neuseeland" },
  { code: "NI", name: "Nicaragua" },
  { code: "NL", name: "Niederlande" },
  { code: "NE", name: "Niger" },
  { code: "NG", name: "Nigeria" },
  { code: "KP", name: "Nordkorea" },
  { code: "MK", name: "Nordmazedonien" },
  { code: "NO", name: "Norwegen" },
  { code: "OM", name: "Oman" },
  { code: "PK", name: "Pakistan" },
  { code: "PS", name: "Palästina" },
  { code: "PA", name: "Panama" },
  { code: "PY", name: "Paraguay" },
  { code: "PE", name: "Peru" },
  { code: "PH", name: "Philippinen" },
  { code: "PL", name: "Polen" },
  { code: "PT", name: "Portugal" },
  { code: "RW", name: "Ruanda" },
  { code: "RO", name: "Rumänien" },
  { code: "RU", name: "Russland" },
  { code: "SA", name: "Saudi-Arabien" },
  { code: "SE", name: "Schweden" },
  { code: "SN", name: "Senegal" },
  { code: "RS", name: "Serbien" },
  { code: "SL", name: "Sierra Leone" },
  { code: "ZW", name: "Simbabwe" },
  { code: "SG", name: "Singapur" },
  { code: "SK", name: "Slowakei" },
  { code: "SI", name: "Slowenien" },
  { code: "SO", name: "Somalia" },
  { code: "ES", name: "Spanien" },
  { code: "LK", name: "Sri Lanka" },
  { code: "ZA", name: "Südafrika" },
  { code: "SD", name: "Sudan" },
  { code: "KR", name: "Südkorea" },
  { code: "SR", name: "Suriname" },
  { code: "SY", name: "Syrien" },
  { code: "TJ", name: "Tadschikistan" },
  { code: "TW", name: "Taiwan" },
  { code: "TZ", name: "Tansania" },
  { code: "TH", name: "Thailand" },
  { code: "TG", name: "Togo" },
  { code: "TT", name: "Trinidad und Tobago" },
  { code: "TD", name: "Tschad" },
  { code: "CZ", name: "Tschechien" },
  { code: "TN", name: "Tunesien" },
  { code: "TR", name: "Türkei" },
  { code: "TM", name: "Turkmenistan" },
  { code: "UG", name: "Uganda" },
  { code: "UA", name: "Ukraine" },
  { code: "HU", name: "Ungarn" },
  { code: "UY", name: "Uruguay" },
  { code: "UZ", name: "Usbekistan" },
  { code: "VU", name: "Vanuatu" },
  { code: "VA", name: "Vatikanstadt" },
  { code: "VE", name: "Venezuela" },
  { code: "AE", name: "Vereinigte Arabische Emirate" },
  { code: "US", name: "Vereinigte Staaten" },
  { code: "GB", name: "Vereinigtes Königreich" },
  { code: "VN", name: "Vietnam" },
  { code: "BY", name: "Weißrussland" },
  { code: "CF", name: "Zentralafrikanische Republik" },
  { code: "CY", name: "Zypern" },
];

const LAND_BY_CODE = new Map(LAENDER.map((l) => [l.code, l.name]));

/**
 * Lesbarer Landname zu einem ISO-Code, fuer Anzeige-Stellen (Konzept § 5 Punkt 4: „Land als
 * lesbaren Namen anzeigen, nicht als rohen ISO-Code"). Unbekannter/leerer Code liefert `null`
 * statt zu raten oder den rohen Code zurueckzugeben — Aufrufer entscheiden selbst ueber den
 * Fallback-Text (analog `BUNDESLAENDER.find(...)?.name ?? d.bundesland` in `settings.tsx`, hier
 * aber bewusst OHNE den `?? code`-Rueckfall, weil ein codierter Zwei-Buchstaben-Wert in der
 * Anzeige mehr verwirrt als ein schlichtes Fehlen).
 */
export function landName(code: string | null | undefined): string | null {
  if (!code) return null;
  return LAND_BY_CODE.get(code.toUpperCase()) ?? null;
}

/**
 * Dieselbe Liste mit englischen Namen, für die englische Angebotsseite (Punkt 7, Nebenfund aus
 * #1284). Namen liefert der Browser (`Intl.DisplayNames`) — keine zweite gepflegte Liste. DE/AT/CH
 * bleiben vorn, der Rest wird nach dem englischen Namen sortiert.
 */
export const LAENDER_EN: Land[] = (() => {
  const namen = new Intl.DisplayNames(["en"], { type: "region" });
  const liste = LAENDER.map((l) => ({ code: l.code, name: namen.of(l.code) ?? l.name }));
  return [...liste.slice(0, 3), ...liste.slice(3).sort((a, b) => a.name.localeCompare(b.name, "en"))];
})();
